// src/components/ServiceHistoryBottomSheet.tsx
import React, { useEffect, useState } from 'react';
import { FlatList, StyleSheet, View, Text } from 'react-native';
import { ActivityIndicator, Chip, Divider, useTheme } from 'react-native-paper';
import BaseBottomSheet from './BaseBottomSheet';
import ServiceService from '../services/serviceService';
import { ServiceRecord, ServiceSchedule } from '../types/Service';
import { ErrorLogger } from '../utils/errorLogger';
import { formatShortDate as formatDate } from '../utils/formatDate';

interface ServiceHistoryBottomSheetProps {
  visible: boolean;
  schedule: ServiceSchedule | null;
  onClose: () => void;
}

const ServiceHistoryBottomSheet: React.FC<ServiceHistoryBottomSheetProps> = ({
  visible,
  schedule,
  onClose,
}) => {
  const theme = useTheme();
  const [records, setRecords] = useState<ServiceRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !schedule?.id) { setRecords([]); return; }
    setLoading(true);
    ServiceService.getRecordsBySchedule(schedule.id)
      .then(setRecords)
      .catch(err => {
        ErrorLogger.logError(err, 'ServiceHistoryBottomSheet');
        setRecords([]);
      })
      .finally(() => setLoading(false));
  }, [visible, schedule?.id]);


  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
    }).format(amount);

  const renderRecord = ({ item }: { item: ServiceRecord }) => (
    <View style={styles.recordItem}>
      <View style={styles.recordHeader}>
        <Text style={[styles.recordDate, { color: theme.colors.onSurface }]}>
          {formatDate(item.performedDate)}
        </Text>
        {item.wasUnexpected && (
          <Chip compact mode="flat" style={styles.unexpectedChip} textStyle={styles.unexpectedText}>
            Unexpected
          </Chip>
        )}
      </View>
      {item.cost !== undefined && (
        <Text style={[styles.recordCost, { color: theme.colors.onSurfaceVariant }]}>
          Cost: {formatAmount(item.cost)}
        </Text>
      )}
      {item.notes ? (
        <Text style={[styles.recordNotes, { color: theme.colors.onSurfaceVariant }]}>
          {item.notes}
        </Text>
      ) : null}
      <Divider style={styles.divider} />
    </View>
  );

  return (
    <BaseBottomSheet
      visible={visible}
      onClose={onClose}
      title={schedule ? `History: ${schedule.serviceType}` : 'Service History'}
      height={0.7}
    >
      {loading ? (
        <View style={styles.loading}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
        </View>
      ) : (
        <FlatList
          data={records}
          renderItem={renderRecord}
          keyExtractor={item => item.id || item.performedDate.toISOString()}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>
                No service records yet
              </Text>
            </View>
          }
          contentContainerStyle={styles.listContent}
        />
      )}
    </BaseBottomSheet>
  );
};

const styles = StyleSheet.create({
  loading: { alignItems: 'center', justifyContent: 'center', padding: 32 },
  listContent: { padding: 16, flexGrow: 1 },
  recordItem: { marginBottom: 4 },
  recordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  recordDate: { fontSize: 15, fontFamily: 'Inter-Medium' },
  recordCost: { fontSize: 13, fontFamily: 'Inter-Regular', marginBottom: 2 },
  recordNotes: { fontSize: 13, fontFamily: 'Inter-Regular', fontStyle: 'italic', marginBottom: 4 },
  divider: { marginVertical: 8 },
  unexpectedChip: { backgroundColor: '#ffb59a', borderRadius: 9999, height: 24 },
  unexpectedText: { color: '#380d00', fontSize: 10, fontFamily: 'Inter-Medium' },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  emptyText: { fontSize: 14 },
});

export default ServiceHistoryBottomSheet;
